import { Controller } from "@hotwired/stimulus";

// The calendar of a moratorium granted at the counter (owner's request of
// 22/10): what is left on the fee chosen, split in equal parts — the rest
// on the last one —, a part every N months from a date. Each line may then
// be changed; the plan is sent only when it adds up to what is left.
// The server makes the same check (CashCollections::GrantMoratorium).
export default class extends Controller {
  static targets = ["due", "total", "count", "every", "from", "rows", "error", "submit"];
  static values = { money: String, mismatch: String, remainings: Object };

  connect() {
    this.draw();
  }

  // What is left on the fee chosen in the design system's select.
  remaining() {
    const input = this.dueTarget.querySelector("input[type=hidden]");
    return parseInt(this.remainingsValue[input ? input.value : ""] || "0", 10);
  }

  draw() {
    const total = this.remaining();
    const count = Math.min(Math.max(parseInt(this.countTarget.value || "1", 10) || 1, 1), 24);
    const every = Math.min(Math.max(parseInt(this.everyTarget.value || "1", 10) || 1, 1), 12);
    const start = this.fromTarget.value ? new Date(`${this.fromTarget.value}T00:00:00`) : new Date();
    const part = Math.floor(total / count);
    this.totalTarget.textContent = this.format(total);
    this.rowsTarget.replaceChildren(...Array.from({ length: count }, (_, index) => {
      const date = this.monthsLater(start, index * every);
      const amount = index === count - 1 ? total - part * (count - 1) : part;
      return this.row(index, this.iso(date), amount);
    }));
    this.check();
  }

  row(index, date, amount) {
    const row = document.createElement("div");
    row.className = "grid gap-2 items-center";
    row.style.gridTemplateColumns = "32px 1fr 1fr 2fr";
    row.dataset.moratoriumRow = String(index + 1);
    row.innerHTML = `
      <span class="ds-section-meta">${index + 1}.</span>
      <input type="text" class="ds-input" name="instalments[][due_date]" value="${date}" required data-controller="flatpickr">
      <input type="number" class="ds-input" name="instalments[][amount]" value="${amount}" min="1" required style="text-align:right;">
      <input type="text" class="ds-input" name="instalments[][label]" maxlength="120">`;
    row.querySelector("input[type=number]").addEventListener("input", () => this.check());
    return row;
  }

  check() {
    const sum = Array.from(this.rowsTarget.querySelectorAll("input[type=number]"))
      .reduce((total, input) => total + (parseInt(input.value || "0", 10) || 0), 0);
    const ok = sum === this.remaining();
    this.errorTarget.hidden = ok;
    this.errorTarget.textContent = ok ? "" : `${this.mismatchValue} ${this.format(sum)} / ${this.format(this.remaining())}`;
    this.submitTarget.disabled = !ok;
  }

  // As the server's `>>`: the 31st of January plus a month is the last day
  // of February, never the 3rd of March.
  monthsLater(start, months) {
    const lastDay = new Date(start.getFullYear(), start.getMonth() + months + 1, 0).getDate();
    return new Date(start.getFullYear(), start.getMonth() + months, Math.min(start.getDate(), lastDay));
  }

  iso(date) {
    const pad = (value) => String(value).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }

  format(amount) {
    return `${String(amount).replace(/\B(?=(\d{3})+(?!\d))/g, " ")} ${this.moneyValue}`;
  }
}
